<script lang="ts" setup>
    import { computed, ref, onMounted } from 'vue'
    import { useLightbox, useImageSizes } from '../composables/useLightbox'

    const props = defineProps<{
        mediaType: string
        url: string
        filename?: string
        /** Size in bytes, when known (e.g. local uploads). */
        size?: number
        loading?: boolean
        error?: string
        removable?: boolean
        /**
         * Dense layout for the composer: a small square thumbnail / slim row,
         * without the metadata caption. The full card is reserved for the
         * message log where the details matter.
         */
        compact?: boolean
    }>()

    defineEmits<{
        remove: []
    }>()

    const isImage = computed(() => props.mediaType.startsWith('image/'))

    // --- File-type identity (icon + accent + human kind label) ---

    const extension = computed(() => {
        const name = props.filename ?? ''
        const dot = name.lastIndexOf('.')
        return dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
    })

    // mediaType subtype, e.g. "pdf" from "application/pdf"
    const subtype = computed(
        () => props.mediaType.split('/')[1]?.split(';')[0] ?? '',
    )

    type FileKind = {
        icon: string
        // CSS color driving the icon glyph + tile tint
        accent: string
        label: string
    }

    const KINDS: Array<{ test: RegExp; kind: FileKind }> = [
        {
            test: /pdf/,
            kind: {
                icon: 'ri:file-pdf-2-line',
                accent: '#e2483d',
                label: 'PDF',
            },
        },
        {
            // Match Word specifically: the generic `document` substring also
            // appears in the XLSX/PPTX OOXML mime types (…officedocument…), so
            // matching it here would mis-label spreadsheets/decks as Word.
            test: /msword|wordprocessingml/,
            kind: {
                icon: 'ri:file-word-2-line',
                accent: '#2b7de9',
                label: 'Word',
            },
        },
        {
            test: /sheet|excel|csv/,
            kind: {
                icon: 'ri:file-excel-2-line',
                accent: '#1f9d57',
                label: 'Spreadsheet',
            },
        },
        {
            test: /presentation|powerpoint/,
            kind: {
                icon: 'ri:file-ppt-2-line',
                accent: '#d98324',
                label: 'Presentation',
            },
        },
        {
            test: /zip|compressed|x-7z|x-rar|x-tar|gzip/,
            kind: {
                icon: 'ri:file-zip-line',
                accent: '#c08a1e',
                label: 'Archive',
            },
        },
        {
            test: /^audio\//,
            kind: {
                icon: 'ri:file-music-line',
                accent: '#8b5cf6',
                label: 'Audio',
            },
        },
        {
            test: /^video\//,
            kind: {
                icon: 'ri:film-line',
                accent: '#6366f1',
                label: 'Video',
            },
        },
        {
            test: /json|javascript|typescript|xml|html|x-sh|x-python/,
            kind: {
                icon: 'ri:file-code-line',
                accent: '#0d9488',
                label: 'Code',
            },
        },
        {
            test: /^text\//,
            kind: {
                icon: 'ri:file-text-line',
                accent: '#64748b',
                label: 'Text',
            },
        },
    ]

    const fileKind = computed<FileKind>(() => {
        const haystack = `${props.mediaType} ${extension.value}`
        for (const { test, kind } of KINDS) {
            if (test.test(haystack)) {
                return kind
            }
        }
        return {
            icon: 'ri:file-line',
            accent: '#64748b',
            label: 'File',
        }
    })

    // Short type tag shown in the meta line: prefer the real extension, fall
    // back to the mime subtype, then the generic kind label.
    const typeTag = computed(() =>
        (
            extension.value ||
            subtype.value ||
            fileKind.value.label
        ).toUpperCase(),
    )

    // --- Size & dimensions ---

    const formattedSize = computed(() => {
        const bytes = props.size
        if (bytes === undefined || bytes < 0) {
            return undefined
        }
        if (bytes < 1024) {
            return `${bytes} B`
        }
        const units = ['KB', 'MB', 'GB', 'TB']
        let value = bytes / 1024
        let unitIndex = 0
        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024
            unitIndex += 1
        }
        return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unitIndex]}`
    })

    // Natural pixel dimensions, captured once the thumbnail decodes.
    const dimensions = ref<{ width: number; height: number }>()
    const onImageLoad = (event: Event) => {
        const img = event.target as HTMLImageElement
        if (img.naturalWidth && img.naturalHeight) {
            dimensions.value = {
                width: img.naturalWidth,
                height: img.naturalHeight,
            }
        }
    }

    // Meta fragments joined with " · " for the caption / doc subtitle.
    const imageMeta = computed(() =>
        [
            dimensions.value &&
                `${dimensions.value.width}×${dimensions.value.height}`,
            formattedSize.value,
        ].filter(Boolean),
    )
    const docMeta = computed(() =>
        [typeTag.value, formattedSize.value].filter(Boolean),
    )

    // --- Lightbox ---

    const galleryRef = ref<HTMLElement>()
    const { preload, getSize } = useImageSizes()
    const { init } = useLightbox({ gallery: galleryRef, loop: false })

    onMounted(async () => {
        if (!isImage.value) {
            return
        }
        preload([{ url: props.url }])
        await init()
    })
</script>

<template>
    <!-- Image attachment: a thumbnail tile with a metadata caption -->
    <figure
        v-if="isImage"
        class="pk-chatbot-file-preview pk-chatbot-file-preview--image"
        :class="{ 'pk-chatbot-file-preview--compact': compact }">
        <div ref="galleryRef" class="pk-chatbot-file-preview__thumb-wrap">
            <a
                :href="url"
                :data-pswp-src="url"
                :data-pswp-width="getSize(url).width"
                :data-pswp-height="getSize(url).height"
                :data-pswp-caption="filename ?? ''"
                class="pk-chatbot-file-preview__thumb-link"
                :aria-label="filename ?? $t('label.file')">
                <img
                    :src="url"
                    :alt="filename"
                    class="pk-chatbot-file-preview__thumb"
                    @load="onImageLoad" />
            </a>
            <span
                v-if="loading"
                class="pk-chatbot-file-preview__status pk-chatbot-file-preview__status--overlay">
                <VvIcon name="line-md:loading-loop" />
            </span>
        </div>
        <figcaption
            v-if="!compact && (filename || imageMeta.length)"
            class="pk-chatbot-file-preview__caption">
            <span
                v-if="filename"
                class="pk-chatbot-file-preview__name"
                :title="filename"
                >{{ filename }}</span
            >
            <span v-if="imageMeta.length" class="pk-chatbot-file-preview__meta">
                <span
                    v-if="error"
                    class="pk-chatbot-file-preview__error"
                    :title="error">
                    <VvIcon name="ri:error-warning-line" />
                    {{ error }}
                </span>
                <template v-else>{{ imageMeta.join(' · ') }}</template>
            </span>
        </figcaption>
        <button
            v-if="removable && !loading"
            type="button"
            class="pk-chatbot-file-preview__remove"
            :title="$t('action.remove')"
            :aria-label="$t('action.remove')"
            @click.prevent="$emit('remove')">
            <VvIcon name="ri:close-line" />
        </button>
    </figure>

    <!-- Document attachment: an icon tile + name + type · size -->
    <div
        v-else
        class="pk-chatbot-file-preview pk-chatbot-file-preview--doc"
        :class="{
            'pk-chatbot-file-preview--error': error,
            'pk-chatbot-file-preview--compact': compact,
        }">
        <span
            class="pk-chatbot-file-preview__icon-tile"
            :style="{ '--file-accent': fileKind.accent }">
            <VvIcon
                v-if="loading"
                name="line-md:loading-loop"
                class="pk-chatbot-file-preview__icon" />
            <VvIcon
                v-else
                :name="fileKind.icon"
                class="pk-chatbot-file-preview__icon" />
        </span>
        <span class="pk-chatbot-file-preview__body">
            <span
                v-if="filename"
                class="pk-chatbot-file-preview__name"
                :title="filename"
                >{{ filename }}</span
            >
            <span class="pk-chatbot-file-preview__meta">
                <span
                    v-if="error"
                    class="pk-chatbot-file-preview__error"
                    :title="error">
                    <VvIcon name="ri:error-warning-line" />
                    {{ error }}
                </span>
                <template v-else>{{ docMeta.join(' · ') }}</template>
            </span>
        </span>
        <a
            v-if="!loading && !error && !compact"
            :href="url"
            target="_blank"
            rel="noopener noreferrer"
            class="pk-chatbot-file-preview__open"
            :title="$t('action.download')"
            :aria-label="`${$t('action.download')}${filename ? ` — ${filename}` : ''}`">
            <VvIcon name="ri:download-2-line" />
        </a>
        <button
            v-if="removable && !loading"
            type="button"
            class="pk-chatbot-file-preview__remove"
            :class="{ 'pk-chatbot-file-preview__remove--inline': !compact }"
            :title="$t('action.remove')"
            :aria-label="$t('action.remove')"
            @click="$emit('remove')">
            <VvIcon name="ri:close-line" />
        </button>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-file-preview {
        // Concentric radii: outer card and inner tiles share an 8px step.
        --file-radius: var(--rounded-lg, 12px);
        position: relative;
        max-width: 280px;
        background-color: var(--color-surface-1);
        border: 1px solid var(--color-surface-3);
        border-radius: var(--file-radius);
        color: var(--color-word-2);
        font-size: var(--text-12);

        &--error {
            border-color: var(--color-danger);
        }

        // --- Document card ---
        &--doc {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            padding: var(--spacing-8);
            min-width: 0;
            transition:
                border-color 0.18s var(--ease-out),
                background-color 0.18s var(--ease-out),
                box-shadow 0.18s var(--ease-out);

            &:hover {
                border-color: var(--color-surface-4);
                background-color: var(--color-surface-2);
                box-shadow: var(--shadow-sm);
            }
        }

        &__icon-tile {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: var(--spacing-36);
            height: var(--spacing-36);
            // One step down from the card radius keeps the nested tile concentric.
            border-radius: var(--rounded-md, 6px);
            // Tint derived from the file-type accent; glyph sits at full accent.
            background-color: color-mix(
                in srgb,
                var(--file-accent) 14%,
                transparent
            );
            color: var(--file-accent);
        }

        &__icon {
            width: var(--spacing-20);
            height: var(--spacing-20);
        }

        &__body {
            display: flex;
            flex-direction: column;
            gap: 1px;
            min-width: 0;
            flex: 1;
        }

        &__open {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            width: var(--spacing-28);
            height: var(--spacing-28);
            margin-inline-start: var(--spacing-4);
            border-radius: var(--rounded);
            color: var(--color-word-3);
            text-decoration: none;
            opacity: 0;
            transition:
                opacity 0.18s var(--ease-out),
                color 0.18s var(--ease-out),
                background-color 0.18s var(--ease-out);

            &:hover {
                background-color: var(--color-surface-3);
                color: var(--color-word-1);
            }
        }

        &--doc:hover &__open,
        &--doc:focus-within &__open {
            opacity: 1;
        }

        // Touch devices have no hover: keep the open affordance visible.
        @media (hover: none) {
            &--doc &__open {
                opacity: 1;
            }
        }

        // --- Image card ---
        &--image {
            display: inline-flex;
            flex-direction: column;
            overflow: hidden;
            margin: 0;
        }

        &__thumb-wrap {
            position: relative;
            display: flex;
        }

        &__thumb-link {
            display: flex;
            cursor: pointer;
            // Subtle outline keeps the image edge crisp on any surface;
            // pure black at low alpha avoids the "dirty edge" of tinted greys.
            box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
        }

        &__thumb {
            display: block;
            width: 100%;
            max-width: 280px;
            max-height: 200px;
            object-fit: cover;
        }

        &__caption {
            display: flex;
            flex-direction: column;
            gap: 1px;
            padding: var(--spacing-8);
            min-width: 0;
        }

        &__name {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: var(--font-medium, 500);
            color: var(--color-word-1);
        }

        &__meta {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: var(--text-12);
            color: var(--color-word-3);
            // Stable column for the dimensions/size figures.
            font-variant-numeric: tabular-nums;
        }

        &__error {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-4);
            color: var(--color-danger);

            .vv-icon {
                flex-shrink: 0;
            }
        }

        &__status {
            display: inline-flex;
            align-items: center;
            justify-content: center;

            &--overlay {
                position: absolute;
                inset: 0;
                background-color: color-mix(
                    in srgb,
                    var(--color-surface-1) 70%,
                    transparent
                );
                color: var(--color-word-1);
            }
        }

        // --- Remove button (input context) ---
        &__remove {
            position: absolute;
            top: var(--spacing-4);
            right: var(--spacing-4);
            display: flex;
            align-items: center;
            justify-content: center;
            // Visible glyph is 18px but the hit area stays a comfortable 28px.
            width: var(--spacing-28);
            height: var(--spacing-28);
            padding: 0;
            border: none;
            border-radius: var(--rounded-full, 9999px);
            background-color: color-mix(
                in srgb,
                var(--color-surface-1) 85%,
                transparent
            );
            color: var(--color-word-2);
            cursor: pointer;
            line-height: 1;
            box-shadow: var(--shadow-sm);
            transition:
                background-color 0.18s var(--ease-out),
                color 0.18s var(--ease-out),
                scale 0.12s var(--ease-out);

            &:hover {
                background-color: var(--color-surface-2);
                color: var(--color-word-1);
            }

            &:active {
                scale: 0.96;
            }

            // Inside the document row the button rides inline, not overlaid
            // (size is inherited from the base).
            &--inline {
                position: static;
                flex-shrink: 0;
                box-shadow: none;
                background-color: transparent;
                color: var(--color-word-4);

                &:hover {
                    background-color: var(--color-surface-3);
                    color: var(--color-word-1);
                }
            }
        }

        // --- Compact (composer) chips ---
        // ChatGPT/Gemini-style: every attachment is the SAME height and carries
        // an always-visible round remove control in the top-right corner — no
        // hover-revealed actions, no size mismatch between image and file chips.
        &--compact {
            // Shared chip height so image and file chips align in the row.
            --chip-size: var(--spacing-56);
            max-width: 240px;

            &.pk-chatbot-file-preview--image {
                width: var(--chip-size);
                height: var(--chip-size);
                border-radius: var(--rounded-md, 6px);
            }

            // Fill the square; the root's `overflow: hidden` rounds the corners.
            .pk-chatbot-file-preview__thumb-wrap,
            .pk-chatbot-file-preview__thumb-link {
                width: 100%;
                height: 100%;
            }

            .pk-chatbot-file-preview__thumb {
                width: 100%;
                height: 100%;
                max-width: none;
                max-height: none;
            }

            &.pk-chatbot-file-preview--doc {
                height: var(--chip-size);
                gap: var(--spacing-8);
                padding: var(--spacing-8);
                // Reserve room so the corner remove button never overlaps the
                // name/meta text.
                padding-inline-end: var(--spacing-28);
            }

            // Square icon tile that fills the chip height, so file chips read at
            // the same scale as image thumbnails.
            .pk-chatbot-file-preview__icon-tile {
                align-self: stretch;
                width: auto;
                height: auto;
                aspect-ratio: 1;
            }

            .pk-chatbot-file-preview__icon {
                width: var(--spacing-20);
                height: var(--spacing-20);
            }
        }

        // Always-visible round remove control in the top-right corner. Sits
        // just inside the chip (an ancestor clips overflow), bigger than before,
        // with an extended hit area for easy tapping.
        &--compact &__remove {
            top: var(--spacing-4);
            right: var(--spacing-4);
            width: var(--spacing-24);
            height: var(--spacing-24);
            border: 1px solid var(--color-surface-4);
            background-color: var(--color-surface);
            color: var(--color-word-1);
            font-size: var(--text-16);
            box-shadow: var(--shadow-sm);

            // Comfortable touch/click target beyond the small visible glyph.
            &::after {
                content: '';
                position: absolute;
                inset: calc(-1 * var(--spacing-4));
            }

            &:hover {
                background-color: var(--color-surface-2);
                color: var(--color-word-1);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            &--doc,
            &__open,
            &__remove {
                transition: none;
            }
        }
    }
</style>
